/**
 * Cycle Design Hub
 *
 * Landing page for the Cycle Design domain. Shows all cycles being configured
 * (draft / paused) with a per-cycle pre-flight readiness checklist, plus a
 * summary row for cycles already in execution.
 *
 * Design activities (S1–S5):
 *   S1 Participation  → configure who participates (Track > Participation tab)
 *   S2 Item Pool      → select items in scope (Item Pools / Track > Item Pool tab)
 *   S3 Criteria       → choose evaluation attributes (Track > Criteria tab)
 *   S4 Value ranges   → lock scales per criterion (Values & Scoring)
 *   S5 Value entry    → confirm required values are filled (Values & Scoring)
 *
 * Once a cycle's pre-flight shows "Ready", the Cycle Owner can launch it to
 * execution where S6 ranking and governance take over.
 */

import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Compass,
  Calendar,
  Layers,
  Target,
  Zap,
  Repeat,
  Infinity as InfinityIcon,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Play,
  GitBranch,
  BarChart3,
  Users,
  Plus,
  ArrowRight,
  FolderOpen,
} from 'lucide-react';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useCycleParticipationStore } from '@/stores/prioritization/cycleParticipationStore';
import { usePinnedCycleStore } from '@/stores/prioritization/pinnedCycleStore';
import { CycleSettingsPanel } from '@/components/prioritization/CycleSettingsPanel';
import {
  computeCycleReadiness,
  type CycleData,
  type CycleReadinessCheck,
  type ReadinessItem,
} from '@/lib/cycleReadiness';
import { CYCLE_MODE_DESCRIPTIONS } from '@/types/prioritization/degapCycle';
import type { DegapCycle, CycleMode } from '@/types/prioritization/degapCycle';
import { useCycleLifecycleDisplayMap } from '@/hooks/prioritization/useCycleLifecycleDisplayMap';
import { CycleLifecycleStatusBadge } from '@/components/prioritization/CycleLifecycleStatusBadge';
import type { CycleLifecycleDisplayStatus } from '@/lib/cycleLifecycleDisplayStatus';

// ─── Cadence badge ────────────────────────────────────────────────────────────

const CADENCE_ICONS: Record<CycleMode, React.ComponentType<{ className?: string }>> = {
  episodic: Zap,
  periodic: Repeat,
  continuous: InfinityIcon,
};

const CADENCE_COLORS: Record<CycleMode, string> = {
  episodic: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
  periodic: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  continuous: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
};

function CadenceBadge({ mode }: { mode: CycleMode }) {
  const Icon = CADENCE_ICONS[mode] ?? Repeat;
  const color = CADENCE_COLORS[mode] ?? 'bg-[var(--surface)] text-[var(--text-sec)] border-[var(--rule)]';
  const label = CYCLE_MODE_DESCRIPTIONS[mode]?.label ?? mode;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

// ─── Readiness score pill ─────────────────────────────────────────────────────

function ReadinessPill({ score, ready }: { score: number; ready: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
        ready
          ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
          : score >= 60
          ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
          : 'bg-[var(--coral-dim)] text-[var(--coral)]'
      }`}
    >
      {ready ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <AlertCircle className="h-3.5 w-3.5" />
      )}
      {score}% ready
    </span>
  );
}

// ─── Readiness item row ───────────────────────────────────────────────────────

function ReadinessRow({ item }: { item: ReadinessItem }) {
  const Icon =
    item.status === 'complete'
      ? CheckCircle2
      : item.status === 'warning'
      ? AlertCircle
      : XCircle;
  const color =
    item.status === 'complete'
      ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
      : item.status === 'warning'
      ? 'text-[var(--mustard)]'
      : 'text-[var(--coral)]';
  const textColor =
    item.status === 'complete' ? 'text-[var(--text-sec)]' : 'text-[var(--text-ter)]';

  return (
    <div className="flex items-start gap-2 py-1">
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${color}`} />
      <div className="min-w-0 flex-1">
        <span className={`text-xs font-medium ${textColor}`}>{item.label}</span>
        {item.message && (
          <span className="text-xs text-[var(--text-ter)] ml-1.5">— {item.message}</span>
        )}
      </div>
      {item.actionUrl && item.status !== 'complete' && (
        <Link
          to={item.actionUrl}
          className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] flex-shrink-0 flex items-center gap-0.5"
        >
          Fix <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}

// ─── Pre-flight checklist (collapsible) ──────────────────────────────────────

function PreflightChecklist({
  readiness,
  cycleId,
}: {
  readiness: CycleReadinessCheck;
  cycleId: string;
}) {
  const [open, setOpen] = useState(false);

  const sections = [
    { label: 'Participants', data: readiness.participantsComplete },
    { label: 'Clarity Plan', data: readiness.clarityPlanComplete },
    { label: 'Output Path', data: readiness.outputPathComplete },
  ];

  const statusLabel = readiness.overallReady
    ? 'All conditions met'
    : readiness.blockers.length > 0
    ? `${readiness.blockers.length} item${readiness.blockers.length !== 1 ? 's' : ''} need attention`
    : readiness.warnings.length > 0
    ? `${readiness.warnings.length} warning${readiness.warnings.length !== 1 ? 's' : ''}`
    : 'Checking…';

  const statusColor = readiness.overallReady
    ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
    : readiness.blockers.length > 0
    ? 'text-[var(--coral)]'
    : 'text-[var(--mustard)]';

  return (
    <div className="mt-3 border border-[var(--rule)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors"
      >
        <span className="flex items-center gap-2">
          <span className="text-xs font-medium text-[var(--text-sec)]">Readiness checklist</span>
          <span className={`text-xs ${statusColor}`}>— {statusLabel}</span>
        </span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        )}
      </button>

      {open && (
        <div className="px-4 py-3 space-y-4 bg-[var(--bg-raised)]">
          {sections.map(({ label, data }) => (
            <div key={label}>
              <div className="flex items-center gap-1.5 mb-1.5">
                {data.ready ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)]" />
                ) : (
                  <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)]" />
                )}
                <span className="text-xs font-semibold text-[var(--text-sec)]">{label}</span>
              </div>
              <div className="pl-5 space-y-0.5">
                {data.items.map((item) => (
                  <ReadinessRow key={item.id} item={item} />
                ))}
              </div>
            </div>
          ))}

          {readiness.nextActions.length > 0 && (
            <div className="pt-2 border-t border-[var(--rule)]">
              <p className="text-xs font-semibold text-[var(--text-sec)] mb-1.5 uppercase tracking-wide">
                Next actions
              </p>
              <div className="space-y-1">
                {readiness.nextActions.map((action, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        action.priority === 'high'
                          ? 'bg-[var(--coral)]'
                          : action.priority === 'medium'
                          ? 'bg-[var(--mustard)]'
                          : 'bg-[var(--rule-bright)]'
                      }`}
                    />
                    <span className="text-[var(--text-sec)]">{action.action}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 border-t border-[var(--rule)]">
            <Link
              to={`/prioritization/cycles/${cycleId}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
            >
              Open cycle detail
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5 flex items-center gap-4">
      <div className={`w-10 h-10  flex items-center justify-center ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-[var(--text-pri)]">{value}</div>
        <div className="text-sm text-[var(--text-ter)]">{label}</div>
      </div>
    </div>
  );
}

// ─── Build CycleData from store state ────────────────────────────────────────

function buildCycleData(
  cycle: DegapCycle,
  sessions: ReturnType<typeof useSessionStore.getState>['sessions'],
  participationMap: Record<string, { cycleId: string; itemId: string; cycleStatus?: string }>,
): CycleData {
  const cycleSessions = sessions.filter((s) => s.cycleId === cycle.id);
  const participationList = Object.values(participationMap ?? {}).filter(
    (p) => p.cycleId === cycle.id,
  );
  const itemIds = participationList.map((p) => p.itemId);
  const itemReadinessMap: CycleData['itemReadinessMap'] = {};
  for (const p of participationList) {
    itemReadinessMap[p.itemId] = {
      hasRequiredValues: p.cycleStatus !== 'nominated',
      missingAttributes: [],
    };
  }

  return {
    id: cycle.id,
    name: cycle.name,
    currentPhase: (cycle as { currentPhase?: string }).currentPhase ?? 'GATHER',
    facilitatorId: (cycle as { facilitatorId?: string }).facilitatorId,
    facilitatorName: (cycle as { facilitatorName?: string }).facilitatorName,
    participantIds: (cycle as { participantIds?: string[] }).participantIds ?? [],
    stakeholderIds: (cycle as { stakeholderIds?: string[] }).stakeholderIds ?? [],
    roleAssignments:
      (cycle as { roleAssignments?: Array<{ userId: string; role: string }> }).roleAssignments ??
      [],
    comparisonSetItemIds: itemIds,
    itemReadinessMap,
    criteriaIds: (cycle as { criteriaIds?: string[] }).criteriaIds ?? [],
    criteriaWeights: (cycle as { criteriaWeights?: Record<string, number> }).criteriaWeights ?? {},
    sessions: cycleSessions.map((s) => ({
      id: s.id,
      name: s.name ?? '',
      scheduledFor: s.scheduledFor ? new Date(s.scheduledFor) : undefined,
      toolId: (s as { toolId?: string }).toolId,
      status: s.state ?? 'draft',
    })),
    targetFocusListId: (cycle as { targetFocusListId?: string }).targetFocusListId,
    targetFocusListName: (cycle as { targetFocusListName?: string }).targetFocusListName,
    decisionMakerId: (cycle as { decisionMakerId?: string }).decisionMakerId,
    decisionMakerName: (cycle as { decisionMakerName?: string }).decisionMakerName,
    leadershipReviewDate: (cycle as { leadershipReviewDate?: string }).leadershipReviewDate
      ? new Date((cycle as { leadershipReviewDate: string }).leadershipReviewDate)
      : undefined,
  };
}

// ─── Cycle design card ────────────────────────────────────────────────────────

function CycleDesignCard({
  cycle,
  trackCount,
  readiness,
  displayStatus,
  displayLoading,
}: {
  cycle: DegapCycle;
  trackCount: number;
  readiness: CycleReadinessCheck;
  displayStatus: CycleLifecycleDisplayStatus;
  displayLoading: boolean;
}) {
  const mode: CycleMode = cycle.cadence?.mode ?? 'episodic';
  const pinnedCycleId = usePinnedCycleStore((s) => s.pinnedCycleId);
  const isActive = pinnedCycleId === cycle.id;

  return (
    <div className={`bg-[var(--bg-raised)]  border overflow-hidden transition-all ${
      isActive ? 'border-[var(--mustard)] shadow-sm' : 'border-[var(--rule)]'
    }`}>
      <div className="px-6 py-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          {/* Left: cycle info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="text-sm font-semibold text-[var(--text-pri)] truncate">{cycle.name}</span>
              <CadenceBadge mode={mode} />
              {displayLoading ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-ter)] min-w-[4.5rem] justify-center animate-pulse">
                  …
                </span>
              ) : (
                <CycleLifecycleStatusBadge status={displayStatus} />
              )}
              {isActive && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--mustard-dim)] text-[var(--mustard)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)]" />
                  Active
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-[var(--text-ter)]">
              <span className="flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" />
                {trackCount} track{trackCount !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <Target className="h-3.5 w-3.5" />
                {readiness.clarityPlanComplete.comparisonSetSize} item
                {readiness.clarityPlanComplete.comparisonSetSize !== 1 ? 's' : ''} in pool
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {readiness.participantsComplete.participantCount} participant
                {readiness.participantsComplete.participantCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Right: readiness pill + actions */}
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            <ReadinessPill score={readiness.overallScore} ready={readiness.overallReady} />

            {/* Configure / Launch */}
            {readiness.overallReady ? (
              <Link
                to={`/prioritization/cycles/${cycle.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] border border-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
              >
                <Play className="h-3.5 w-3.5" />
                Launch
              </Link>
            ) : (
              <Link
                to={`/prioritization/cycles/${cycle.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors"
              >
                Configure
                <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        </div>

        <PreflightChecklist readiness={readiness} cycleId={cycle.id} />
      </div>
    </div>
  );
}

// ─── Design activities quick-links ────────────────────────────────────────────

const DESIGN_ACTIVITIES = [
  {
    step: 'S1',
    label: 'Participation',
    description: 'Who takes part in each track',
    href: '/prioritization/cycles',
    icon: Users,
    color: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  },
  {
    step: 'S2',
    label: 'Collections',
    description: 'Stage candidate items before locking',
    href: '/prioritization/collections',
    icon: FolderOpen,
    color: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  },
  {
    step: 'S3',
    label: 'Criteria',
    description: 'Evaluation attributes per track',
    href: '/prioritization/settings/attributes',
    icon: Target,
    color: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  },
  {
    step: 'S4/S5',
    label: 'Values & Scoring',
    description: 'Lock ranges and confirm entries',
    href: '/prioritization/scoring',
    icon: BarChart3,
    color: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  },
  {
    step: 'WF',
    label: 'Templates & Workflows',
    description: 'Select template and workflow policy',
    href: '/prioritization/settings/templates',
    icon: GitBranch,
    color: 'bg-[var(--coral-dim)] text-[var(--coral)]',
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CycleDesign() {
  const navigate = useNavigate();
  const [panelOpen, setPanelOpen] = useState(false);

  const pinnedCycleId = usePinnedCycleStore((s) => s.pinnedCycleId);

  const cycles = useDegapProcessStore((state) => state.cycles);
  const tracks = useTrackStore((state) => state.tracks);
  const sessions = useSessionStore((state) => state.sessions);
  const rawParticipation = useCycleParticipationStore((state) => state.participation);
  const participationMap = useMemo(
    () =>
      (rawParticipation ?? {}) as Record<string, { cycleId: string; itemId: string; cycleStatus?: string }>,
    [rawParticipation],
  );

  const cycleIdList = useMemo(() => cycles.map((c) => c.id), [cycles]);
  const { displayByCycleId, loading: lifecycleDisplayLoading } = useCycleLifecycleDisplayMap(cycleIdList);

  // Partition by DB-derived lifecycle display (falls back to store while the first fetch runs).
  const designCycles = useMemo(() => {
    return cycles.filter((c) => {
      const d = displayByCycleId[c.id];
      if (lifecycleDisplayLoading && d === undefined) {
        return c.status !== 'active' && c.status !== 'completed' && c.status !== 'archived';
      }
      return d === 'draft' || d === 'paused';
    });
  }, [cycles, displayByCycleId, lifecycleDisplayLoading]);

  const activeCycles = useMemo(() => {
    return cycles.filter((c) => {
      const d = displayByCycleId[c.id];
      if (lifecycleDisplayLoading && d === undefined) {
        return c.status === 'active';
      }
      return d === 'active' || d === 'ratified' || d === 'published' || d === 'closed';
    });
  }, [cycles, displayByCycleId, lifecycleDisplayLoading]);

  // Build readiness for each design cycle
  const cycleReadiness = useMemo(
    () =>
      designCycles.map((cycle) => {
        const data = buildCycleData(cycle, sessions, participationMap);
        return { cycle, readiness: computeCycleReadiness(data) };
      }),
    [designCycles, sessions, participationMap],
  );

  const totalTracks = useMemo(
    () => designCycles.reduce((sum, c) => sum + tracks.filter((t) => t.cycleId === c.id).length, 0),
    [designCycles, tracks],
  );

  const readyCycles = cycleReadiness.filter((r) => r.readiness.overallReady).length;

  return (
    <div className="min-h-full bg-[var(--surface)]">
      {/* ─── Page header ──────────────────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)] px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[var(--slate-dim)] flex items-center justify-center">
              <Compass className="h-6 w-6 text-[var(--text-ter)]" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[var(--text-pri)]">Cycle Design</h1>
              <p className="text-sm text-[var(--text-ter)] mt-0.5">
                Configure cycles before execution — participants, item pools, criteria, values, and
                workflow template.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Cycle
          </button>
        </div>
      </div>

      <div className="px-8 py-6 space-y-8">
        {/* ─── Stats row ──────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            label="Cycles in Design"
            value={designCycles.length}
            icon={Compass}
            color="bg-[var(--slate-dim)] text-[var(--text-sec)]"
          />
          <StatCard
            label="Configured Tracks"
            value={totalTracks}
            icon={Layers}
            color="bg-[var(--slate-dim)] text-[var(--text-sec)]"
          />
          <StatCard
            label="Ready to Launch"
            value={readyCycles}
            icon={Play}
            color={readyCycles > 0 ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'}
          />
        </div>

        {/* ─── Design activities quick-links ──────────────────────────────────── */}
        <div>
          <h2 className="text-sm font-semibold text-[var(--text-sec)] mb-3">Design Activities</h2>
          <div className="grid grid-cols-5 gap-3">
            {DESIGN_ACTIVITIES.map((activity) => (
              <Link
                key={activity.step}
                to={activity.href}
                className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 hover:border-[var(--slate)] hover:shadow-sm transition-all group"
              >
                <div
                  className={`w-8 h-8  flex items-center justify-center mb-3 ${activity.color}`}
                >
                  <activity.icon className="h-4 w-4" />
                </div>
                <div className="text-xs font-bold text-[var(--text-ter)] mb-0.5">{activity.step}</div>
                <div className="text-sm font-semibold text-[var(--text-pri)] group-hover:text-[var(--text-ter)] transition-colors">
                  {activity.label}
                </div>
                <div className="text-xs text-[var(--text-ter)] mt-0.5 leading-tight">
                  {activity.description}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* ─── Cycles in design ───────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[var(--text-sec)]">Cycles in Design</h2>
            <Link
              to="/prioritization/cycles"
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] flex items-center gap-1"
            >
              All cycles <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {designCycles.length === 0 ? (
            <div className="bg-[var(--bg-raised)] border border-dashed border-[var(--rule)] p-16 flex flex-col items-center gap-4 text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--slate-dim)] flex items-center justify-center">
                <Compass className="h-8 w-8 text-[var(--text-ter)]" />
              </div>
              <div>
                <p className="text-lg font-semibold text-[var(--text-sec)]">No cycles in design</p>
                <p className="text-sm text-[var(--text-ter)] mt-1">
                  Create a new cycle to start configuring tracks, participants, and criteria.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] transition-colors"
              >
                <Plus className="h-4 w-4" />
                New Cycle
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {cycleReadiness.map(({ cycle, readiness }) => (
                <CycleDesignCard
                  key={cycle.id}
                  cycle={cycle}
                  trackCount={tracks.filter((t) => t.cycleId === cycle.id).length}
                  readiness={readiness}
                  displayStatus={displayByCycleId[cycle.id] ?? 'draft'}
                  displayLoading={lifecycleDisplayLoading && displayByCycleId[cycle.id] === undefined}
                />
              ))}
            </div>
          )}
        </div>

        {/* ─── Cycles in execution ────────────────────────────────────────────── */}
        {activeCycles.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[var(--text-sec)]">In Execution</h2>
              <Link
                to="/prioritization/cycles"
                className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] flex items-center gap-1"
              >
                All cycles <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="space-y-2">
              {activeCycles.map((cycle) => {
                const isActive = pinnedCycleId === cycle.id;
                return (
                  <div
                    key={cycle.id}
                    className={`bg-[var(--bg-raised)]  border px-6 py-4 flex items-center justify-between gap-4 ${
                      isActive ? 'border-[var(--mustard)] shadow-sm' : 'border-[var(--green)]'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
                          <Play className="h-3 w-3" />
                          Executing
                        </span>
                        {isActive && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--mustard-dim)] text-[var(--mustard)]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)]" />
                            Active
                          </span>
                        )}
                        {lifecycleDisplayLoading && displayByCycleId[cycle.id] === undefined ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-ter)] min-w-[4.5rem] justify-center animate-pulse">
                            …
                          </span>
                        ) : (
                          <CycleLifecycleStatusBadge status={displayByCycleId[cycle.id] ?? 'active'} />
                        )}
                        <span className="text-sm font-semibold text-[var(--text-pri)] truncate">
                          {cycle.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-[var(--text-ter)]">
                        <span className="flex items-center gap-1">
                          <Layers className="h-3.5 w-3.5" />
                          {tracks.filter((t) => t.cycleId === cycle.id).length} tracks
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {sessions.filter((s) => s.cycleId === cycle.id).length} sessions
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Link
                        to={`/prioritization/cycles/${cycle.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] hover:bg-[var(--green-dim)] transition-colors"
                      >
                        View execution
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ─── Cycle settings panel ─────────────────────────────────────────────── */}
      <CycleSettingsPanel
        mode="create"
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onCreated={(newId) => { setPanelOpen(false); navigate(`/prioritization/cycles/${newId}`); }}
      />
    </div>
  );
}
